import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  DatePicker,
  Radio,
  Checkbox,
  InputNumber,
  Switch,
  Tabs,
  Upload,
  message,
  Popconfirm,
  Divider,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

interface PopupRecord {
  id: number;
  name: {
    vi: string;
    en: string;
    zh: string;
  };
  popupType: string;
  popupStyle: string;
  sortPriority: number;
  enabled: boolean;
  startTime?: string;
  endTime?: string;
  remark?: string;
  activityType?: string;
  popupWeight?: number;
  userScope: {
    type: 'all' | 'filter' | 'recharge' | 'nonRecharge' | 'reRecharge';
    vipLevels?: number[];
  };
  channelScope: {
    type: 'all' | 'filter';
    channels?: string[];
  };
  imageUrl?: string;
  jumpType: string;
  jumpLink?: string;
  richText?: {
    vi?: string;
    en?: string;
    zh?: string;
  };
  experimentGroup?: string; // 实验组/对照组
  requiredTags?: number[]; // 标签ID数组，可选
  popupFrequency?: {
    dailyCount: number; // 每天弹几次
    minInterval: number; // 每次最少间隔多久（分钟）
  };
  pushSettings?: {
    pushTime: 'allDay' | 'timePeriod';
    pushTimeStart?: string;
    pushTimeEnd?: string;
    pushFrequency: 'once' | 'interval' | 'multiple';
    pushInterval?: number;
    pushDevice: 'all' | 'ios' | 'android';
    pushUser: 'all' | 'loggedIn' | 'specific' | 'newVersion';
    placementChannels: string[];
    switchSpeed: number;
  };
  creator: string;
  createTime: string;
  updater: string;
  updateTime: string;
}

const POPUP_TYPE_OPTIONS = ['公告', '活动', '系统通知', '优惠', '其他'];
const POPUP_STYLE_OPTIONS = ['图文不限', '纯文字', '纯图片', '视频'];
const JUMP_TYPE_OPTIONS = ['不跳转', 'H5链接', 'APP页面', '内部页面'];
const USER_SCOPE_TYPES = [
  { value: 'all', label: '全部用户 (包含游客)' },
  { value: 'filter', label: '筛选可见用户' },
  { value: 'recharge', label: '充值用户' },
  { value: 'nonRecharge', label: '非充值用户' },
  { value: 'reRecharge', label: '复充用户' },
];

const VIP_LEVELS = Array.from({ length: 21 }, (_, i) => i); // 0-20

// 模拟标签数据（从用户标签管理获取）
const MOCK_TAGS = [
  { id: 1, name: 'VIP1', color: '#ff4d4f' },
  { id: 2, name: 'VIP2', color: '#ff7a45' },
  { id: 3, name: '活跃用户', color: '#52c41a' },
  { id: 4, name: '沉默用户', color: '#8c8c8c' },
  { id: 5, name: '高价值用户', color: '#1890ff' },
  { id: 6, name: '新用户', color: '#722ed1' },
];

// 模拟数据
const mockRecords: PopupRecord[] = [
  {
    id: 1,
    name: {
      vi: '老用户找回',
      en: 'Old user retrieval',
      zh: '老用户包雨',
    },
    popupType: '活动',
    popupStyle: '图文不限',
    sortPriority: 10,
    enabled: true,
    startTime: '2025-11-01 00:00:00',
    endTime: '2025-12-31 23:59:59',
    remark: '老用户召回活动',
    userScope: {
      type: 'all',
    },
    channelScope: {
      type: 'all',
    },
    jumpType: 'H5链接',
    jumpLink: 'https://example.com/activity',
    experimentGroup: '实验组',
    requiredTags: [1, 2], // VIP1和VIP2标签
    creator: 'admin',
    createTime: '2025-11-01 10:00:00',
    updater: 'admin',
    updateTime: '2025-11-01 10:00:00',
  },
  {
    id: 2,
    name: {
      vi: '直播间在线励',
      en: 'Live room online time',
      zh: '直播间长奖励',
    },
    popupType: '活动',
    popupStyle: '图文不限',
    sortPriority: 8,
    enabled: true,
    userScope: {
      type: 'filter',
      vipLevels: [1, 2, 3],
    },
    channelScope: {
      type: 'filter',
      channels: ['default', 'Egypt1'],
    },
    jumpType: '不跳转',
    experimentGroup: '对照组',
    requiredTags: [3], // 活跃用户标签
    creator: 'admin',
    createTime: '2025-11-02 10:00:00',
    updater: 'admin',
    updateTime: '2025-11-02 10:00:00',
  },
  {
    id: 3,
    name: {
      vi: '新手弹',
      en: 'New user pop-up',
      zh: '新手',
    },
    popupType: '公告',
    popupStyle: '纯文字',
    sortPriority: 5,
    enabled: false,
    userScope: {
      type: 'recharge',
    },
    channelScope: {
      type: 'all',
    },
    jumpType: 'APP页面',
    experimentGroup: '实验组',
    requiredTags: [6], // 新用户标签
    creator: 'admin',
    createTime: '2025-11-03 10:00:00',
    updater: 'admin',
    updateTime: '2025-11-03 10:00:00',
  },
];

const PopupConfig: React.FC = () => {
  const [form] = Form.useForm();
  const [editorForm] = Form.useForm();
  const [records, setRecords] = useState<PopupRecord[]>(mockRecords);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PopupRecord | null>(null);
  const [searchText, setSearchText] = useState('');
  const [userScopeType, setUserScopeType] = useState<string>('all');
  const [channelScopeType, setChannelScopeType] = useState<string>('all');
  const pushTime = Form.useWatch(['pushSettings', 'pushTime'], editorForm);
  const pushFrequency = Form.useWatch(['pushSettings', 'pushFrequency'], editorForm);
  const jumpType = Form.useWatch('jumpType', editorForm);

  const handleAdd = () => {
    setEditingRecord(null);
    editorForm.resetFields();
    editorForm.setFieldsValue({
      enabled: true,
      sortPriority: 10,
      userScopeType: 'all',
      channelScopeType: 'all',
      jumpType: '不跳转',
      popupType: '公告',
      popupStyle: '图文不限',
      popupFrequency: {
        dailyCount: 1,
        minInterval: 60,
      },
    });
    setDrawerVisible(true);
  };

  const handleEdit = (record: PopupRecord) => {
    setEditingRecord(record);
    const values: any = {
      ...record,
      nameVi: record.name.vi,
      nameEn: record.name.en,
      nameZh: record.name.zh,
      userScopeType: record.userScope.type,
      channelScopeType: record.channelScope.type,
      vipLevels: record.userScope.vipLevels || [],
      channels: record.channelScope.channels || [],
      requiredTags: record.requiredTags || [],
      timeRange: record.startTime && record.endTime
        ? [dayjs(record.startTime), dayjs(record.endTime)]
        : null,
      richTextVi: record.richText?.vi,
      richTextEn: record.richText?.en,
      richTextZh: record.richText?.zh,
      popupFrequency: record.popupFrequency || {
        dailyCount: 1,
        minInterval: 60,
      },
      pushSettings: record.pushSettings ? {
        ...record.pushSettings,
        pushTimeRange: record.pushSettings.pushTimeStart && record.pushSettings.pushTimeEnd
          ? [dayjs(record.pushSettings.pushTimeStart, 'HH:mm'), dayjs(record.pushSettings.pushTimeEnd, 'HH:mm')]
          : null,
      } : {
        pushTime: 'allDay',
        pushFrequency: 'once',
        pushDevice: 'all',
        pushUser: 'all',
        placementChannels: [],
        switchSpeed: 0,
      },
    };
    editorForm.setFieldsValue(values);
    setUserScopeType(record.userScope.type);
    setChannelScopeType(record.channelScope.type);
    setDrawerVisible(true);
  };

  const handleDelete = (record: PopupRecord) => {
    setRecords(prev => prev.filter(item => item.id !== record.id));
    message.success('弹窗配置已删除');
  };

  const handleSubmit = async () => {
    try {
      const values = await editorForm.validateFields();
      const payload: PopupRecord = {
        id: editingRecord ? editingRecord.id : Date.now(),
        name: {
          vi: values.nameVi || '',
          en: values.nameEn || '',
          zh: values.nameZh || '',
        },
        popupType: values.popupType,
        popupStyle: values.popupStyle,
        sortPriority: values.sortPriority,
        enabled: values.enabled,
        startTime: values.timeRange?.[0]?.format('YYYY-MM-DD HH:mm:ss'),
        endTime: values.timeRange?.[1]?.format('YYYY-MM-DD HH:mm:ss'),
        remark: values.remark,
        activityType: values.activityType,
        popupWeight: values.popupWeight,
        userScope: {
          type: values.userScopeType,
          vipLevels: values.userScopeType === 'filter' ? values.vipLevels : undefined,
        },
        channelScope: {
          type: values.channelScopeType,
          channels: values.channelScopeType === 'filter' ? values.channels : undefined,
        },
        imageUrl: values.imageUrl,
        jumpType: values.jumpType,
        jumpLink: values.jumpLink,
        richText: {
          vi: values.richTextVi,
          en: values.richTextEn,
          zh: values.richTextZh,
        },
        experimentGroup: values.experimentGroup,
        requiredTags: values.requiredTags || undefined,
        popupFrequency: values.popupFrequency ? {
          dailyCount: values.popupFrequency.dailyCount,
          minInterval: values.popupFrequency.minInterval,
        } : undefined,
        pushSettings: values.pushSettings ? {
          pushTime: values.pushSettings.pushTime,
          pushTimeStart: values.pushSettings.pushTimeRange?.[0]?.format('HH:mm'),
          pushTimeEnd: values.pushSettings.pushTimeRange?.[1]?.format('HH:mm'),
          pushFrequency: values.pushSettings.pushFrequency,
          pushInterval: values.pushSettings.pushInterval,
          pushDevice: values.pushSettings.pushDevice,
          pushUser: values.pushSettings.pushUser,
          placementChannels: values.pushSettings.placementChannels || [],
          switchSpeed: values.pushSettings.switchSpeed || 0,
        } : undefined,
        creator: editingRecord ? editingRecord.creator : 'admin',
        createTime: editingRecord ? editingRecord.createTime : dayjs().format('YYYY-MM-DD HH:mm:ss'),
        updater: 'admin',
        updateTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      };

      setRecords(prev => {
        if (editingRecord) {
          return prev.map(item => (item.id === editingRecord.id ? payload : item));
        }
        return [payload, ...prev];
      });

      message.success(editingRecord ? '弹窗配置已更新' : '弹窗配置已创建');
      setDrawerVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const filteredData = records.filter(item =>
    item.name.vi.toLowerCase().includes(searchText.toLowerCase()) ||
    item.name.en.toLowerCase().includes(searchText.toLowerCase()) ||
    item.name.zh.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: '序号',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: '弹窗名称',
      key: 'name',
      width: 300,
      render: (_: any, record: PopupRecord) => (
        <div>
          <div><strong>越南语:</strong> {record.name.vi}</div>
          <div><strong>英语:</strong> {record.name.en}</div>
          <div><strong>中文(繁体):</strong> {record.name.zh}</div>
        </div>
      ),
    },
    {
      title: '弹框类型',
      dataIndex: 'popupType',
      key: 'popupType',
      width: 120,
    },
    {
      title: '弹框样式',
      dataIndex: 'popupStyle',
      key: 'popupStyle',
      width: 120,
    },
    {
      title: '排序优先级',
      dataIndex: 'sortPriority',
      key: 'sortPriority',
      width: 120,
      sorter: (a: PopupRecord, b: PopupRecord) => a.sortPriority - b.sortPriority,
    },
    {
      title: '启用状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'success' : 'default'}>
          {enabled ? '开启' : '关闭'}
        </Tag>
      ),
    },
    {
      title: '显示时间',
      key: 'time',
      width: 200,
      render: (_: any, record: PopupRecord) => (
        <div>
          {record.startTime ? (
            <>
              <div>开始: {record.startTime}</div>
              <div>结束: {record.endTime || '长期有效'}</div>
            </>
          ) : (
            <span>长期有效</span>
          )}
        </div>
      ),
    },
    {
      title: '实验组/对照组',
      dataIndex: 'experimentGroup',
      key: 'experimentGroup',
      width: 120,
      render: (group: string) => group ? <Tag>{group}</Tag> : '-',
    },
    {
      title: '标签限制',
      dataIndex: 'requiredTags',
      key: 'requiredTags',
      width: 200,
      render: (tags: number[] | undefined) => {
        if (!tags || tags.length === 0) {
          return <span style={{ color: '#999' }}>无限制</span>;
        }
        return (
          <Space size={[0, 8]} wrap>
            {tags.map(tagId => {
              const tag = MOCK_TAGS.find(t => t.id === tagId);
              return tag ? (
                <Tag key={tagId} color={tag.color}>{tag.name}</Tag>
              ) : null;
            })}
          </Space>
        );
      },
    },
    {
      title: '创建人',
      dataIndex: 'creator',
      key: 'creator',
      width: 100,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 170,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: PopupRecord) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确认删除该弹窗配置？"
            onConfirm={() => handleDelete(record)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="弹窗配置"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => message.info('已刷新')}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增
            </Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="搜索弹窗名称"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
        </div>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={{
            total: filteredData.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          scroll={{ x: 1500 }}
        />
      </Card>

      <Modal
        title={editingRecord ? '编辑弹窗配置' : '新增弹窗配置'}
        open={drawerVisible}
        onCancel={() => setDrawerVisible(false)}
        onOk={handleSubmit}
        width={1200}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={editorForm}
          layout="vertical"
          initialValues={{
            enabled: true,
            sortPriority: 10,
            userScopeType: 'all',
            channelScopeType: 'all',
            jumpType: '不跳转',
            popupType: '公告',
            popupStyle: '图文不限',
            popupFrequency: {
              dailyCount: 1,
              minInterval: 60,
            },
          }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="弹框类型 (用于前端显示弹框标题)"
                name="popupType"
                rules={[{ required: true, message: '请选择弹框类型' }]}
              >
                <Select>
                  {POPUP_TYPE_OPTIONS.map(type => (
                    <Option key={type} value={type}>{type}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="弹框样式"
                name="popupStyle"
                rules={[{ required: true, message: '请选择弹框样式' }]}
              >
                <Select>
                  {POPUP_STYLE_OPTIONS.map(style => (
                    <Option key={style} value={style}>{style}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="排序优先级 (数值越大越靠前)"
                name="sortPriority"
                rules={[{ required: true, message: '请输入排序优先级' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="启用状态"
                name="enabled"
                valuePropName="checked"
              >
                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="显示时间 (留空长期生效)"
                name="timeRange"
              >
                <RangePicker
                  showTime
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD HH:mm:ss"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="活动类型"
                name="activityType"
              >
                <Select allowClear placeholder="请选择活动类型">
                  <Option value="新手引导">新手引导</Option>
                  <Option value="注册转盘">注册转盘</Option>
                  <Option value="红包雨">红包雨</Option>
                  <Option value="每日签到">每日签到</Option>
                  <Option value="在线时长奖励">在线时长奖励</Option>
                  <Option value="下载APP">下载APP</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="弹窗权重"
                name="popupWeight"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="实验组/对照组"
                name="experimentGroup"
              >
                <Select allowClear>
                  <Option value="实验组">实验组</Option>
                  <Option value="对照组">对照组</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">弹窗频率配置</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="每天弹几次"
                name={['popupFrequency', 'dailyCount']}
                rules={[
                  { required: true, message: '请输入每天弹窗次数' },
                  { type: 'number', min: 1, message: '每天至少弹窗1次' },
                ]}
                extra="设置每天最多弹窗的次数"
              >
                <InputNumber
                  min={1}
                  max={100}
                  style={{ width: '100%' }}
                  placeholder="请输入每天弹窗次数"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="每次最少间隔多久 (分钟)"
                name={['popupFrequency', 'minInterval']}
                rules={[
                  { required: true, message: '请输入弹窗间隔时间' },
                  { type: 'number', min: 1, message: '间隔时间至少1分钟' },
                ]}
                extra="设置两次弹窗之间的最小间隔时间"
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                  placeholder="请输入间隔时间（分钟）"
                  addonAfter="分钟"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="备注说明 (仅后台列表可见)"
            name="remark"
          >
            <TextArea
              rows={3}
              placeholder="请输入备注说明,可留空"
            />
          </Form.Item>

          <Divider orientation="left">哪些用户可见</Divider>
          <Form.Item
            name="userScopeType"
            rules={[{ required: true, message: '请选择用户可见范围' }]}
          >
            <Radio.Group onChange={(e) => setUserScopeType(e.target.value)}>
              {USER_SCOPE_TYPES.map(type => (
                <Radio key={type.value} value={type.value}>{type.label}</Radio>
              ))}
            </Radio.Group>
          </Form.Item>
          {userScopeType === 'filter' && (
            <Form.Item
              label="选择VIP等级"
              name="vipLevels"
              rules={[{ required: true, message: '请至少选择一个VIP等级' }]}
            >
              <Checkbox.Group>
                <Row gutter={[8, 8]}>
                  {VIP_LEVELS.map(level => (
                    <Col key={level} span={4}>
                      <Checkbox value={level}>VIP{level}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Form.Item>
          )}

          <Divider orientation="left">哪些渠道可见</Divider>
          <Form.Item
            name="channelScopeType"
            rules={[{ required: true, message: '请选择渠道可见范围' }]}
          >
            <Radio.Group onChange={(e) => setChannelScopeType(e.target.value)}>
              <Radio value="all">全部渠道</Radio>
              <Radio value="filter">筛选可见渠道</Radio>
            </Radio.Group>
          </Form.Item>
          {channelScopeType === 'filter' && (
            <Form.Item
              label="选择渠道"
              name="channels"
              rules={[{ required: true, message: '请至少选择一个渠道' }]}
            >
              <Checkbox.Group>
                <Row gutter={[8, 8]}>
                  {['default', 'default-埃及投流', 'Egypt1', 'Egypt2', 'Egypt3', 'Egypt4', 'Egypt5', 'default-duanxin', 'default-ws', 'Egypt-pwa2', 'default-1749393264'].map(channel => (
                    <Col key={channel} span={6}>
                      <Checkbox value={channel}>{channel}</Checkbox>
                    </Col>
                  ))}
                </Row>
              </Checkbox.Group>
            </Form.Item>
          )}

          <Divider orientation="left">标签选择（可选）</Divider>
          <Form.Item 
            label="标签限制" 
            name="requiredTags"
            extra="选择指定标签后，只有带标签的用户才能触发弹窗。可以不选择，也可以选择多个标签。"
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="请选择标签（可选）"
              showSearch
              optionLabelProp="label"
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {MOCK_TAGS.map(tag => (
                <Option key={tag.id} value={tag.id} label={tag.name}>
                  <Tag color={tag.color}>{tag.name}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Divider orientation="left">图片配置</Divider>
          <Form.Item
            label="图片 (370*132)"
            name="imageUrl"
          >
            <Upload
              listType="picture-card"
              maxCount={1}
              beforeUpload={() => false}
            >
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>上传</div>
              </div>
            </Upload>
            <div style={{ marginTop: 8, color: '#999', fontSize: 12 }}>
              只支持webp格式,大小不能超过50KB
            </div>
          </Form.Item>

          <Form.Item
            label="跳转类型"
            name="jumpType"
            rules={[{ required: true, message: '请选择跳转类型' }]}
          >
            <Select>
              {JUMP_TYPE_OPTIONS.map(type => (
                <Option key={type} value={type}>{type}</Option>
              ))}
            </Select>
          </Form.Item>

          {jumpType && jumpType !== '不跳转' && (
            <Form.Item
              label={jumpType === 'H5链接' ? '跳转链接' : '跳转页面'}
              name="jumpLink"
              rules={[{ required: true, message: '请输入跳转链接' }]}
            >
              <Input placeholder={jumpType === 'H5链接' ? '请填写H5跳转链接' : '请填写APP页面路径'} />
            </Form.Item>
          )}

          <Divider orientation="left">推送设置</Divider>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="推送时间"
                name={['pushSettings', 'pushTime']}
                initialValue="allDay"
              >
                <Radio.Group>
                  <Radio value="allDay">全天</Radio>
                  <Radio value="timePeriod">时间段</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            {pushTime === 'timePeriod' && (
              <Col span={16}>
                <Form.Item
                  label="推送时间段"
                  name={['pushSettings', 'pushTimeRange']}
                >
                  <RangePicker
                    picker="time"
                    style={{ width: '100%' }}
                    format="HH:mm"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label={
                  <Space>
                    <span>推送频次</span>
                    <span style={{ color: '#999', fontSize: 12 }}>?</span>
                  </Space>
                }
                name={['pushSettings', 'pushFrequency']}
                initialValue="once"
              >
                <Radio.Group>
                  <Radio value="once">仅一次</Radio>
                  <Radio value="interval">间隔时长</Radio>
                  <Radio value="multiple">多次</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            {pushFrequency === 'interval' && (
              <Col span={8}>
                <Form.Item
                  label="间隔时长(小时)"
                  name={['pushSettings', 'pushInterval']}
                  rules={[{ required: true, message: '请输入间隔时长' }]}
                >
                  <InputNumber min={1} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            )}
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="推送设备"
                name={['pushSettings', 'pushDevice']}
                initialValue="all"
              >
                <Radio.Group>
                  <Radio value="all">全部</Radio>
                  <Radio value="ios">IOS</Radio>
                  <Radio value="android">安卓</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                label={
                  <Space>
                    <span>推送用户</span>
                    <span style={{ color: '#999', fontSize: 12 }}>?</span>
                  </Space>
                }
                name={['pushSettings', 'pushUser']}
                initialValue="all"
              >
                <Radio.Group>
                  <Radio value="all">全部用户</Radio>
                  <Radio value="loggedIn">已登录用户</Radio>
                  <Radio value="specific">特定用户</Radio>
                  <Radio value="newVersion">新版本用户定义</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="投放渠道和页面"
            name={['pushSettings', 'placementChannels']}
            rules={[{ required: true, message: '请至少选择一个投放渠道' }]}
          >
            <Checkbox.Group>
              <Row gutter={[16, 8]}>
                <Col span={8}>
                  <Checkbox value="H5首页">H5首页</Checkbox>
                </Col>
                <Col span={8}>
                  <Checkbox value="H5游戏页">H5游戏页</Checkbox>
                </Col>
                <Col span={8}>
                  <Checkbox value="H5直播页">H5直播页</Checkbox>
                </Col>
                <Col span={8}>
                  <Checkbox value="APP首页">APP首页</Checkbox>
                </Col>
                <Col span={8}>
                  <Checkbox value="APP游戏页">APP游戏页</Checkbox>
                </Col>
                <Col span={8}>
                  <Checkbox value="APP直播页">APP直播页</Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="弹窗切换速度(秒)"
                name={['pushSettings', 'switchSpeed']}
                initialValue={0}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">语言配置</Divider>
          <Tabs defaultActiveKey="vi">
            <TabPane tab="越南语" key="vi">
              <Form.Item
                label="弹窗名称"
                name="nameVi"
                rules={[{ required: true, message: '请输入越南语弹窗名称' }]}
              >
                <Input placeholder="请填写弹窗名称" />
              </Form.Item>
              <Form.Item
                label="富文本内容"
                name="richTextVi"
              >
                <TextArea rows={6} placeholder="请输入富文本内容" />
              </Form.Item>
            </TabPane>
            <TabPane tab="英语" key="en">
              <Form.Item
                label="弹窗名称"
                name="nameEn"
                rules={[{ required: true, message: '请输入英语弹窗名称' }]}
              >
                <Input placeholder="请填写弹窗名称" />
              </Form.Item>
              <Form.Item
                label="富文本内容"
                name="richTextEn"
              >
                <TextArea rows={6} placeholder="请输入富文本内容" />
              </Form.Item>
            </TabPane>
            <TabPane tab="中文(繁体)" key="zh">
              <Form.Item
                label="弹窗名称"
                name="nameZh"
                rules={[{ required: true, message: '请输入中文弹窗名称' }]}
              >
                <Input placeholder="请填写弹窗名称" />
              </Form.Item>
              <Form.Item
                label="富文本内容"
                name="richTextZh"
              >
                <TextArea rows={6} placeholder="请输入富文本内容" />
              </Form.Item>
            </TabPane>
          </Tabs>
        </Form>
      </Modal>
    </div>
  );
};

export default PopupConfig;

